/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
"use client";

import { usePathname } from "next/navigation";
import PropTypes from "prop-types";
import { useEffect, useRef, useState } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getDeliveryAreasAsKey } from "../../../api/locationLock";
import {
  INTERNATIONAL_PF_COOKIE,
  LAST_VISITED_ADDRESS,
  LAST_VISITED_INTERNATIONAL_CITY,
  LAST_VISITED_INTERNATIONAL_LOCATION,
  LAST_VISITED_PINCODE,
  LAST_VISITED_PLACE_TYPES,
  PF_COOKIE,
  SELECTED_INTERNATIONAL_CITY,
} from "../../../constants/storage";
import { calDiscountPercentage, getProductDeliveryType, isJsonString, pageName } from "../../../utils/common";
import CookieUtil from "../../../utils/storage/cookieUtil";
import PincodeLocationSuggestion from "../integratedGoogleSuggestionField";
import InternationalCitySuggestion from "../internationalCitySuggestion";
import CountryDropdown from "./countryDropdown";
import {
  getInternationalCityList,
  setIsHideSavedAddresses,
  setSelectedCountryAction,
  getCountryListsData,
  getInternationalCityListPDP,
  setSelectedIntLocation,
  setIsShowMobileLocationLockAction,
  setSelectedCountryFromList,
  setIsShowMobileLocationLockDrawerAction,
  setSelectedDomesticLocation,
  setIsShowCartEmpty,
  setIsClearCartDueToCountryChange,
  setPendingCountryChange,
  setShouldClearLocationLockInput,
  setSelectedDomesticLocationForCart,
  setSelectedIntLocationForCart,
  setUnserviceableCartItems,
} from "../../../store/slices/locationLock";
import { COUNTRIES, INTERNATIONAL } from "../../../constants/common/common";
import { requestToFetchProductListingAction } from "../../../store/slices/plpSlice";
import { plpPageConstants } from "../../../constants/pageConstants/plpConstants";
import isEmpty from "lodash.isempty";
import { desktopIndiaConfig } from "../../../constants/mockData/mockData";
import { removeEmptyNullAndUndefinedFromObject } from "../../../utils/common";
import { setCartItems, setIsLoaderVisible } from "../../../store/slices/cartSlice";
import { cleverTapCommonEvents, validationFor } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { AUTO_TRIGGER_LOCK, PLEASE_ENTER_DELIVERY_LOCATION } from "../../../constants/common/locationFieldConstants";
import { CAKE_TYPE } from "../../../constants/pageConstants/pdpConstants";
import {
  hasCartItemsFromDifferentCatalog,
  resolveClearCartModeForLocation,
  validateCartServiceabilityOnLocationChange,
} from "../../../utils/locationLock/validateCartServiceabilityOnLocationChange";
import { showCleverTapNotification } from "../../../analytics/useCleverTap";
import { getCleverTapPageType } from "../../../utils/fnpPageType";
import { requestMobileStoreFeed } from "../../../store/slices/dynamicHomeSlice";
import {
  commasSeparatedIndexes,
  getConfirmedLocationSource,
  getGateAutoTriggerFlag,
  getLocationSavedTrackingFields,
} from "../../../utils/locationLock";
import { requestToSetCartItemDetailsOnDA } from "../../../store/slices/deliveryAddress";
import { SERVICEABILITY_LOCATION_ERROR } from "../../../constants/pageConstants/deliveryAddressPageConstant";
import { checkCartServiceabilityStatus, isLowAccuracyPlaceSelected } from "../../../utils/checkout/deliveryAddress";
import { getCartService } from "../../../api/cartApi";
import { getLatLongFromSavedAddressSync } from "../../../store/sagas/helpers/savedAddressLatLong";
import { isHomePathname } from "../../../utils/dynamicHomeUtils/storeUrl";

/**
 * Description placeholder
 * @date 26/03/2024 - 13:45:26
 *
 * @export
 * @returns {string}
 */
export default function LocationLock({
  isFromCheckout,
  fullProductDetails,
  onShowHideLocationLock,
  isFromPDP = false,
  onCountrySelection,
  isLocationError,
  messageText,
  messageClass,
  isDisable = false,
  setMandatoryError = () => {},
  isInternationalMobilePDP = false,
  isSeparateFieldsDesign = false,
  isContinueButtonHidden = false,
  onClearSavedAddressSelection,
  isProductUnavailable = false,
  onExploreAvailableProducts,
  onSuggestionsVisibilityChange,
  isFromOutsideDrawer = false,
  isFromGiftFinderGate = false,
  locationCaptureMode,
  capturedLocation,
}) {
  const isJoinedFieldLayout = (isFromPDP || isInternationalMobilePDP) && !isSeparateFieldsDesign;
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const pathname = usePathname();

  /** Active masthead brand from Redux — used to re-fetch feed after location save. */
  const activeStore = useSelector((state) => {
    const stores = state.dynamicHome?.pageStores ?? [];
    return stores.find((s) => s.isSelected) ?? stores[0] ?? null;
  });
  const activeStoreId = activeStore?.storeId ?? null;
  const activeStoreName = activeStore?.storeName || activeStore?.name || null;
  const isSearchPath = pathname?.includes("/search");
  const completePathName = typeof window !== "undefined" && window.location.href ? window.location.href : null;
  const {
    appliedFilters,
    appliedSort,
    isMobile,
    catalogId,
    countryListData,
    visitedCountry,
    configData,
    product,
    cartCount,
    completeCartDetails,
    cartItems,
    savedAddresses,
  } = useSelector(
    (state) => ({
      appliedFilters: state?.plp?.appliedFilters,
      appliedSort: state?.plp?.appliedSort,
      isMobile: state?.appConfig?.isMobile,
      catalogId: state?.appConfig?.catalogId,
      countryListData: state?.locationLock?.countryListData,
      visitedCountry: state?.locationLock?.visitedCountry,
      configData: state?.locationLock?.configData,
      product: state?.pdp?.clientCurrentClickedItem,
      cartCount: state?.cart?.cartCount,
      completeCartDetails: state?.deliveryAddress?.completeCartDetails,
      cartItems: state?.cart?.cartItems,
      savedAddresses: state?.savedAddress?.savedAddresses || [],
    }),
    shallowEqual,
  );
  const pincodeError = useSelector((state) => state.pdp.pincodeError);
  const selectedDomesticLocation = useSelector((state) => state?.locationLock?.selectedDomesticLocation);
  const selectedIntLocation = useSelector((state) => state?.locationLock.selectedIntLocation);
  const clientVariantType = useSelector((state) => state?.pdp?.clientVariantType);
  const shouldClearLocationLockInput = useSelector((state) => state?.locationLock?.shouldClearLocationLockInput);

  const [showCountryList, setShowCountryList] = useState(false);
  const isInternationalCountry =
    fullProductDetails?.primaryCategoryId === INTERNATIONAL ||
    fullProductDetails?.primaryProductCategoryId === INTERNATIONAL;
  const selectedIntlCity = CookieUtil.getCookie(SELECTED_INTERNATIONAL_CITY);
  const lastVisitedAddress = CookieUtil.getCookie("lastVisitedAddress");
  const lastVisitedPincode = CookieUtil.getCookie("lastVisitedPincode");
  const lastVisitedIntLocation = CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_LOCATION);
  const lastVisitedIntPincode = CookieUtil.getCookie(LAST_VISITED_INTERNATIONAL_CITY);
  const urlIdentifier = product?.urlIdentifier;
  const [searchValue, setSearchValue] = useState("");
  const [suggestionSelected, setSuggestionSelected] = useState(false);
  const [showDeliveryCityList, setShowDeliveryCityList] = useState(true);
  const [isLocationNudgeVisible, setIsLocationNudgeVisible] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(
    isEmpty(visitedCountry) ? desktopIndiaConfig.defaultCountry : visitedCountry,
  );
  // Tracks a country pick made through the country-list UI in this component's lifetime,
  // so the "Sending gift to X, Confirm to redirect" banner only shows after that explicit
  // action — not when selectedCountry briefly disagrees with catalogId while visitedCountry
  // hydrates on initial page load.
  const hasManuallyChangedCountryRef = useRef(false);
  const [serviceabilityErrorMessage, setServiceabilityErrorMessage] = useState("");
  const isHome = isHomePathname(pathname);
  const isPLP = pathname?.includes("-lp");
  const urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();

  const {
    isAutoGateMandateOnDesktopHome,
    isAutoGateMandateOnDesktopPLP,
    isAutoGateMandateOnMobileHome,
    isAutoGateMandateOnMobilePLP,
    isAutoGateEnabledOnDesktopHome,
    isAutoGateEnabledOnMobileHome,
    isAutoGateEnabledOnDesktopPLP,
    isAutoGateEnabledOnMobilePLP,
    mandateGatePLPList,
  } = configData;
  const isMandateForDesktop =
    ((isAutoGateMandateOnDesktopHome && isHome) || (isAutoGateMandateOnDesktopPLP && isPLP)) &&
    catalogId === COUNTRIES.INDIA;
  const isMandateForMobile =
    ((isAutoGateMandateOnMobileHome && isHome) || (isAutoGateMandateOnMobilePLP && isPLP)) &&
    catalogId === COUNTRIES.INDIA;
  const isAutoTriggerLock = getGateAutoTriggerFlag(mandateGatePLPList);
  const isAutoForDesktop =
    !isAutoTriggerLock && ((isAutoGateEnabledOnDesktopHome && isHome) || (isAutoGateEnabledOnDesktopPLP && isPLP));
  const isAutoForMobile =
    !isAutoTriggerLock && ((isAutoGateEnabledOnMobileHome && isHome) || (isAutoGateEnabledOnMobilePLP && isPLP));
  const commonPageData = {
    page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    mandatory:
      isAutoTriggerLock || selectedCountry.catelogID !== COUNTRIES.INDIA
        ? false
        : isMobile
          ? isMandateForMobile
          : isMandateForDesktop,
    auto: isAutoTriggerLock ? false : isMobile ? isAutoForMobile : isAutoForDesktop,
    user_initiated: isAutoTriggerLock
      ? true
      : isMobile
        ? !(isAutoForMobile || isMandateForMobile)
        : !(isAutoForDesktop || isMandateForDesktop),
    clean_url: pathname,
    complete_url: completePathName,
  };

  const isInternational = catalogId !== COUNTRIES.INDIA;
  const discountPercentage = calDiscountPercentage(
    fullProductDetails?.productPrice?.listPrice,
    fullProductDetails?.productPrice?.price,
  );
  const productDeliveryType = getProductDeliveryType(fullProductDetails?.primaryCategoryId);
  const cleverTapPDPCommonData = {
    page_name: pageName(isHome, isPLP, pathname, fullProductDetails?.urlIdentifier),
    pageType: (typeof window !== "undefined" && window.fnpPageType) || "",
    product_name: isFromCheckout ? "" : fullProductDetails?.productName,
    pid: isFromCheckout ? "" : fullProductDetails?.productId,
    product_category: fullProductDetails?.primaryCategoryId,
    product_price: isFromCheckout ? "" : fullProductDetails?.productPrice?.price,
    is_variant_available: isFromCheckout ? "" : fullProductDetails?.isVariant === "Y",
    is_egg: isFromCheckout ? "" : (clientVariantType?.toUpperCase() === CAKE_TYPE.WITH_EGG ?? false),
    delivery_type: isFromCheckout ? "" : productDeliveryType,
    is_international: isInternational,
    city: isInternational ? lastVisitedIntLocation : "",
    area: isInternational ? "" : lastVisitedAddress,
    pincode: isInternational ? "" : lastVisitedPincode,
    system_pincode: isInternational ? "" : lastVisitedPincode,
    country: catalogId,
    strike_price: isFromCheckout ? "" : fullProductDetails?.productPrice?.listPrice,
    discount_percentage: discountPercentage,
    product_rating: isFromCheckout ? "" : fullProductDetails?.productReviewsAndRatings?.ratingValue,
    product_review_count: isFromCheckout ? "" : fullProductDetails?.productReviewsAndRatings?.totalReview,
    is_product_available: isFromCheckout ? "" : fullProductDetails?.stockAvailable,
    sku_id: isFromCheckout ? "" : fullProductDetails?.skuCode,
    clean_url: pathname,
    complete_url: completePathName,
  };
  const pageLocationSavedData = {
    ...commonPageData,
    area: null,
    pincode: null,
    system_pincode: null,
    country: null,
    city: null,
  };
  const pageLocationCountryData = { ...commonPageData, country: null };
  const ishomePlpPdpLocationData = isHome || isPLP ? pageLocationSavedData : cleverTapPDPCommonData;

  /**
   * Builds CleverTap locationSaved payload with page_type (not pageType).
   * @param {Object} overrides - Event-specific fields
   * @returns {Object}
   */
  const buildLocationSavedCleverTapData = (overrides = {}) => {
    const { pageType, ...restLocationData } = ishomePlpPdpLocationData || {};
    return {
      ...restLocationData,
      page_type: getCleverTapPageType(pageType || restLocationData?.page_type),
      flow: "new",
      ...overrides,
    };
  };

  const countryId = isEmpty(fullProductDetails?.countryGeoId)
    ? selectedCountry?.countryId
    : fullProductDetails?.countryGeoId;

  const isCountryChanged =
    hasManuallyChangedCountryRef.current &&
    !isEmpty(selectedCountry?.catelogID) &&
    selectedCountry?.catelogID !== catalogId;

  useEffect(() => {
    if (showCountryList) {
      dispatch(setIsHideSavedAddresses(true));
    }
  }, [showCountryList]);

  useEffect(() => {
    if (searchValue?.length) {
      setMandatoryError?.(false);
      setServiceabilityErrorMessage("");
    }
  }, [searchValue]);

  useEffect(() => {
    if (!shouldClearLocationLockInput) {
      return;
    }
    setSearchValue("");
    setSuggestionSelected(false);
    dispatch(setSelectedDomesticLocation({}));
    dispatch(setSelectedIntLocation({}));
    dispatch(setShouldClearLocationLockInput(false));
    const locationInput =
      document.getElementById("pincode-location-input") || document.getElementById("city-suggestion-input");
    locationInput?.focus();
  }, [shouldClearLocationLockInput, dispatch]);

  useEffect(() => {
    if (Array.isArray(countryListData)) {
      const filterData = countryListData?.filter((item) => {
        return item?.catelogID === catalogId;
      })?.[0];

      dispatch(setSelectedCountryAction(filterData));
    }
  }, [countryListData, catalogId]);

  useEffect(() => {
    if (isEmpty(countryListData)) {
      dispatch(getCountryListsData());
    }
    if (isFromPDP && !isMobile) {
      showCleverTapNotification();
    }
  }, []);

  useEffect(() => {
    const shouldSetSelectedCountryPDP =
      !isEmpty(fullProductDetails?.productId) &&
      isInternationalCountry &&
      (isFromPDP || isInternationalMobilePDP) &&
      visitedCountry;

    const shouldSetSelectedCountryFallback =
      !isEmpty(visitedCountry?.countryId) && catalogId !== COUNTRIES.INDIA && (!isFromPDP || !isInternationalMobilePDP);

    // A captured country wins: it is what the user picked inside the gate, and it can
    // differ from the site's catalog (they chose USA while browsing India). The country
    // list is the source of truth for the object shape, so resolve from it by catalog id
    // rather than trusting the stored copy.
    const capturedCountry =
      locationCaptureMode && capturedLocation?.geoId && Array.isArray(countryListData)
        ? countryListData.find((c) => c?.catelogID === capturedLocation.geoId)
        : null;

    if (capturedCountry) {
      setSelectedCountry(capturedCountry);
      dispatch(setSelectedCountryFromList(capturedCountry?.catelogID));
      if (capturedCountry?.catelogID !== COUNTRIES.INDIA) {
        dispatch(getInternationalCityList(capturedCountry?.countryId));
      }
      return;
    }

    if (shouldSetSelectedCountryPDP) {
      setSelectedCountry(visitedCountry);
      dispatch(getInternationalCityList(visitedCountry?.countryId));
      dispatch(setSelectedCountryFromList(visitedCountry?.catelogID));
    } else if (shouldSetSelectedCountryFallback) {
      setSelectedCountry(visitedCountry);
      dispatch(setSelectedCountryFromList(visitedCountry?.catelogID));
      setMandatoryError(false);
      dispatch(getInternationalCityList(visitedCountry?.countryId));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullProductDetails?.productId, isInternationalCountry, visitedCountry, capturedLocation?.geoId, countryListData]);

  /**
   * Retrieves the default selected country from the country list based on the catalog ID.
   * If no catalog ID is found in the visited country, the function defaults to the provided catalog ID.
   *
   * @returns {Object | undefined} The default selected country or undefined if no match is found.
   * @date 08/11/2024 - 14:00:00
   */
  const updateLocationLockData = () => {
    if (Array.isArray(countryListData)) {
      const defaultSelectedCountry = countryListData?.filter((country) => {
        const getVerifycatelogID = isEmpty(selectedCountry?.catelogID) ? catalogId : selectedCountry?.catelogID;

        return country.catelogID === getVerifycatelogID;
      });

      if (defaultSelectedCountry?.length > 0) {
        return defaultSelectedCountry?.[0];
      }
    }
  };

  /**
   * Deletes the cookies related to the last visited international location and city
   * @param {Object} country - The country object containing the catalog ID.
   */
  const deleteCookiePDPCookies = (country) => {
    CookieUtil.deleteCookie(LAST_VISITED_INTERNATIONAL_LOCATION, { path: `/${country?.catelogID}/gift` });
    CookieUtil.deleteCookie(LAST_VISITED_INTERNATIONAL_CITY, { path: `/${country?.catelogID}/gift` });
  };

  /**
   * It will set the auto trigger time in cookie only for Homepage, PLP.
   * @param {*} autoTriggerLockTime
   */
  const setAutoTriggerCookies = (autoTriggerLockTime) => {
    if ((isHome || isPLP) && autoTriggerLockTime) {
      CookieUtil.setCookie(AUTO_TRIGGER_LOCK, autoTriggerLockTime.toString(), {
        maxAge: autoTriggerLockTime,
        path: "/",
        encode: decodeURIComponent,
        secure: true,
      });
    }
  };

  /**
   * Sets the updated pincode for the cart item.
   * @param {string} pincode - The pincode to set for the cart item.
   */
  const setUpdatedPincodeForCart = (pincode) => {
    const formData = new FormData();
    const payload = {
      itemIndex: commasSeparatedIndexes(cartCount),
      pincode,
    };
    Object.entries(payload).forEach(([key, value]) => formData.append(key, value));
    dispatch(requestToSetCartItemDetailsOnDA(new URLSearchParams(formData).toString()));
  };

  /**
   * Checks checkout serviceability for a pincode before updating cart location.
   *
   * @param {string} pincode
   * @returns {Promise<"error"|"warning"|"success"|null>}
   */
  const getCheckoutServiceabilityStatus = async (pincode) => {
    const serviceabilityResult = await checkCartServiceabilityStatus(completeCartDetails?.orderList, pincode);
    return serviceabilityResult?.status ?? null;
  };

  /**
   * Description placeholder
   * @date 28/03/2024 - 15:17:29
   */
  const onHandleContinueShopping = async (locationDetails) => {
    !isMobile && showCleverTapNotification();
    const { pincode, location } = locationDetails;

    if (isInternationalCountry && isEmpty(location)) {
      return;
    }
    if (isEmpty(location) && searchValue) {
      return;
    }

    const isSwitchingCountry = !isEmpty(selectedCountry?.catelogID) && selectedCountry.catelogID !== catalogId;

    // Country change with cart items: show ClearCart first; redirect only after Change Country.
    if (isSwitchingCountry && cartCount > 0 && !isFromCheckout) {
      if (!Array.isArray(cartItems) || cartItems.length === 0) {
        try {
          const isDomestic = catalogId === COUNTRIES.INDIA;
          const latLong = getLatLongFromSavedAddressSync(savedAddresses);
          const cartResponse = await getCartService({ payload: "Y", isDomestic, ...latLong });
          if (cartResponse?.data?.orderList?.length > 0) {
            dispatch(setCartItems(cartResponse.data.orderList));
          }
        } catch (error) {
          console.log(error);
        }
      }

      const redirectUrl =
        selectedCountry.catelogID === COUNTRIES.INDIA
          ? "/"
          : `/${selectedCountry?.countryCategoryID || selectedCountry?.categoryId}-lp?promo=mob_countryList`;

      dispatch(
        setPendingCountryChange({
          redirectUrl,
          locationDetails: { pincode, location },
          selectedCountry,
          countryCookieKey: countryId,
          isInternationalTarget: selectedCountry.catelogID !== COUNTRIES.INDIA,
        }),
      );
      dispatch(setIsClearCartDueToCountryChange(true));
      dispatch(setIsShowCartEmpty(true));
      return false;
    }

    // Cart has items from another catalog while already on this country page (manual URL).
    // Show ClearCart with Change Location → clear cart, then apply city.
    if (cartCount > 0 && !isFromCheckout && !isSwitchingCountry) {
      let orderList = Array.isArray(cartItems) ? cartItems : [];
      if (!orderList.length) {
        try {
          const isDomestic = catalogId === COUNTRIES.INDIA;
          const latLong = getLatLongFromSavedAddressSync(savedAddresses);
          const cartResponse = await getCartService({ payload: "Y", isDomestic, ...latLong });
          orderList = cartResponse?.data?.orderList || [];
          if (orderList.length > 0) {
            dispatch(setCartItems(orderList));
          }
        } catch (error) {
          console.log(error);
        }
      }

      if (hasCartItemsFromDifferentCatalog(orderList, catalogId)) {
        if (catalogId === COUNTRIES.INDIA) {
          dispatch(
            setSelectedDomesticLocationForCart({
              pincode,
              address: location,
            }),
          );
        } else {
          dispatch(
            setSelectedIntLocationForCart({
              pincode,
              address: location,
            }),
          );
        }
        dispatch(setIsClearCartDueToCountryChange(true));
        dispatch(setIsShowCartEmpty(true));
        return false;
      }
    }

    if (isInternationalCountry || selectedCountry.catelogID !== COUNTRIES.INDIA) {
      if (isFromCheckout && pincode) {
        const serviceabilityStatus = await getCheckoutServiceabilityStatus(pincode);
        if (serviceabilityStatus === "error") {
          setServiceabilityErrorMessage(SERVICEABILITY_LOCATION_ERROR);
          return false;
        }
        setServiceabilityErrorMessage("");
      } else if (!isFromCheckout && pincode && cartCount > 0) {
        // Same as domestic: validate cart via /serviceability/v1/products/serviceable before applying city.
        const validationResult = await validateCartServiceabilityOnLocationChange({
          selectedPincode: pincode,
          cartItemCount: cartCount,
          catalogId,
          savedAddresses,
          isFromCheckout: false,
          isCurrentProductServiceable: !(isFromPDP && isProductUnavailable),
        });

        if (Array.isArray(validationResult?.orderList) && validationResult.orderList.length > 0) {
          dispatch(setCartItems(validationResult.orderList));
        }

        if (!validationResult?.shouldProceed) {
          const { isCountryChange, unserviceableItems } = resolveClearCartModeForLocation({
            orderList: validationResult?.orderList,
            catalogId,
            serviceabilityItems: validationResult?.serviceabilityResult?.items,
          });
          dispatch(setIsClearCartDueToCountryChange(isCountryChange));
          dispatch(setUnserviceableCartItems(unserviceableItems));
          dispatch(
            setSelectedIntLocationForCart({
              pincode,
              address: location,
            }),
          );
          dispatch(setIsShowCartEmpty(true));
          return false;
        }

        dispatch(setUnserviceableCartItems([]));
      }

      const options = { maxAge: 60 * 60 * 24 * 7, path: "/", encode: decodeURIComponent };

      const country = updateLocationLockData(locationDetails.pincode);

      const selectedCountriesInCookie =
        selectedIntlCity && isJsonString(selectedIntlCity) ? JSON.parse(selectedIntlCity) : {};
      const data = {
        ...selectedCountriesInCookie,
        [countryId]: {
          country: country.countryName,
          categoryId: `${country?.catelogID}/gifts`,
          catalogId: country?.catelogID,
          countryId: country?.countryId,
          countryName: country.countryName,
        },
      };

      data[countryId].city = location;
      data[countryId].cityId = pincode;

      if (!isEmpty(location)) {
        const response = await getDeliveryAreasAsKey(pincode);

        if (response?.isSuccess) {
          CookieUtil.setCookie(INTERNATIONAL_PF_COOKIE, response?.cacheKey, options);

          CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_LOCATION, location, options);

          CookieUtil.setCookie(LAST_VISITED_INTERNATIONAL_CITY, pincode, options);
        } else {
          CookieUtil.deleteCookie(INTERNATIONAL_PF_COOKIE, options);
          CookieUtil.deleteCookie(SELECTED_INTERNATIONAL_CITY, options);
          deleteCookiePDPCookies(country);
        }
        if (!isFromCheckout) {
          dispatch(setSelectedIntLocation(locationDetails));
        }
      } else {
        deleteCookiePDPCookies(country);
      }

      if (isFromCheckout && !isEmpty(locationDetails)) {
        dispatch(setSelectedIntLocation(locationDetails));
      }

      CookieUtil.setCookie(SELECTED_INTERNATIONAL_CITY, JSON.stringify(data), options);

      dispatch(setSelectedCountryAction(selectedCountry));

      if (selectedCountry.catelogID !== catalogId) {
        window.location.href = `/${selectedCountry?.countryCategoryID || selectedCountry?.categoryId}-lp?promo=mob_countryList`;
        dispatch(setIsShowMobileLocationLockAction(false));
      } else {
        dispatch(setIsLoaderVisible(true));
        if (isPLP || isSearchPath) {
          window.scrollTo({ top: 0 });
          const email = CookieUtil.getCookie("faul") || "";
          const fnpId = CookieUtil.getCookie("ab_user_id");
          const qs = isSearchPath && urlParams.has("qs") ? urlParams.get("qs") : null;
          const commonParams = {
            page: 0,
            size: isMobile ? plpPageConstants.MOBILE_PRODUCT_PER_PAGE : plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
            geoId: catalogId,
            pincode: pincode,
            filters: appliedFilters,
            sortBy: appliedSort?.sortBy,
            sortOrder: appliedSort?.sortOrder,
            email,
            fnpId,
          };
          const actionParams = qs
            ? { ...commonParams, pageType: plpPageConstants.SEARCH, qs }
            : { ...commonParams, categoryUrl: `fnp.com${pathname}` };

          dispatch(requestToFetchProductListingAction(removeEmptyNullAndUndefinedFromObject(actionParams)));
        }
      }
      trackEvent(
        cleverTapCommonEvents.locationSaved,
        buildLocationSavedCleverTapData({
          country: country?.countryName,
          city: JSON.parse(selectedIntlCity)?.city,
        }),
        ["clevertap"],
      );
    } else {
      if (isFromCheckout && pincode) {
        const serviceabilityStatus = await getCheckoutServiceabilityStatus(pincode);
        if (serviceabilityStatus === "error") {
          setServiceabilityErrorMessage(SERVICEABILITY_LOCATION_ERROR);
          return false;
        }
        setServiceabilityErrorMessage("");
      }

      if (isFromCheckout && !isEmpty(locationDetails)) {
        dispatch(
          setSelectedDomesticLocation({
            pincode: locationDetails?.pincode,
            address: locationDetails?.location,
          }),
        );
      }

      dispatch(setSelectedCountryAction(selectedCountry));

      // A pincode is what makes the rest of this branch meaningful: the delivery-areas
      // lookup, the cookies and the feed/listing refetches all key off it. Confirming
      // with none is the "forget my location" path, which skips the lookup entirely —
      // getDeliveryAreasAsKey(undefined) cannot succeed, so nesting the reset under its
      // response left it unreachable.
      const hasLocation = !isEmpty(locationDetails) && !isEmpty(locationDetails?.pincode);
      const options = { maxAge: 60 * 24 * 60 * 30, path: "/", encode: decodeURIComponent };

      if (!hasLocation) {
        // An untouched field means "not ready" rather than "clear it", so searchValue
        // guards the reset.
        if (!searchValue) {
          CookieUtil.deleteCookie(PF_COOKIE, options);
          CookieUtil.deleteCookie(LAST_VISITED_PINCODE, options);
          CookieUtil.deleteCookie(LAST_VISITED_ADDRESS, options);
          dispatch(setSelectedDomesticLocation({}));
        }
        // Otherwise the field has text but no resolved pincode, so there is nothing to
        // commit and nothing to clear — the stored location stays as it was. Callers are
        // expected to resolve a restored prefill before confirming (see
        // getLocationDetailsForContinue in integratedGoogleSuggestionField); reaching here
        // with text in the field means the confirm is a deliberate no-op.
      } else {
        const response = await getDeliveryAreasAsKey(pincode);

        if (response?.isSuccess) {
          if (response?.cacheKey) CookieUtil.setCookie(PF_COOKIE, response?.cacheKey, options);
          CookieUtil.setCookie(LAST_VISITED_PINCODE, locationDetails?.pincode, options);
          CookieUtil.setCookie(LAST_VISITED_ADDRESS, locationDetails?.location, options);
          trackEvent(
            cleverTapCommonEvents.locationSaved,
            buildLocationSavedCleverTapData({
              area: location,
              pincode,
              system_pincode: pincode,
              ...getLocationSavedTrackingFields(getConfirmedLocationSource()),
            }),
            ["clevertap"],
          );

          // Confirming inside the quiz is still a real location change for whatever page
          // the drawer was opened over, so the page behind it refreshes exactly as it
          // would from its own location strip.
          if (isHome && isMobile) {
            // Get email from faul cookie
            const email = CookieUtil.getCookie("faul") || "";
            const fnpId = CookieUtil.getCookie("ab_user_id");
            dispatch(
              requestMobileStoreFeed({
                pincode,
                email,
                fnpId,
                storeId: activeStoreId,
                storeName: activeStoreName,
                forceCall: true,
              }),
            );
          }

          if (isPLP || isSearchPath) {
            window.scrollTo({ top: 0 });
            const email = CookieUtil.getCookie("faul") || "";
            const fnpId = CookieUtil.getCookie("ab_user_id");
            const qs = isSearchPath && urlParams.has("qs") ? urlParams.get("qs") : null;
            const commonParams = {
              page: 0,
              size: isMobile ? plpPageConstants.MOBILE_PRODUCT_PER_PAGE : plpPageConstants.DESKTOP_PRODUCT_PER_PAGE,
              geoId: COUNTRIES?.INDIA,
              pincode: pincode,
              filters: appliedFilters,
              sortBy: appliedSort?.sortBy,
              sortOrder: appliedSort?.sortOrder,
              email,
              fnpId,
            };
            const actionParams = qs
              ? { ...commonParams, pageType: plpPageConstants.SEARCH, qs }
              : { ...commonParams, categoryUrl: `fnp.com${pathname}` };
            dispatch(requestToFetchProductListingAction(removeEmptyNullAndUndefinedFromObject(actionParams)));
          }
        }
      }

      // Switching back to India from an international catalog has to leave the
      // international page, whether or not a pincode came with it. This sits outside the
      // `hasLocation` split on purpose: it used to live under the delivery-areas response,
      // which only exists when a pincode was entered, so confirming India with an empty
      // field silently kept the user on the international site.
      if (catalogId !== COUNTRIES.INDIA && selectedCountry?.catelogID === COUNTRIES.INDIA) {
        dispatch(setIsShowMobileLocationLockAction(false));
        window.location.href = `/`;
        return;
      }
    }

    if (isFromCheckout && cartCount > 0) {
      setUpdatedPincodeForCart(pincode);
    }
    setAutoTriggerCookies(configData?.autoTriggerLock);
    onShowHideLocationLock?.(false);
    if (isFromCheckout) {
      dispatch(setIsShowMobileLocationLockAction(false));
    }
  };

  /**
   * Record the chosen location instead of committing it.
   *
   * Country is resolved here rather than in the field children: `selectedCountry` is this
   * component's state, and the international field's `locationDetails` carries only
   * `{pincode, location}`. The whole country object travels, not just its catalog id —
   * the caller writes the per-country city cookie, which is keyed by it.
   *
   * `updateLocationLockData` is the same resolver the commit uses, so the captured country
   * is the one from the country-list API rather than the local default config — those
   * disagree on `countryId`, which is the key that cookie is written under.
   *
   * @param {{pincode?: string, location?: string}} locationDetails
   * @returns {void}
   */
  const onCaptureLocation = (locationDetails) => {
    const country = updateLocationLockData() ?? selectedCountry;

    locationCaptureMode?.onCapture?.({
      pincode: locationDetails?.pincode || "",
      // The lock calls the area `location`; the captured answer calls it `address`, the
      // same name the cookie and redux use.
      address: locationDetails?.location || "",
      isInternational: country?.catelogID !== COUNTRIES.INDIA,
      geoId: country?.catelogID,
      country,
    });
  };

  /**
   *
   * @param {object} item handle all callbacks with value item
   */
  const onCountrySelectionHandler = (item) => {
    hasManuallyChangedCountryRef.current = true;
    setShowDeliveryCityList(true);
    onClearSavedAddressSelection?.();
    if ((isFromPDP || isInternationalMobilePDP) && onCountrySelection) {
      onCountrySelection?.(item);
      dispatch(getInternationalCityListPDP(fullProductDetails?.productId));
      trackEvent(
        cleverTapCommonEvents.locationCountrySelected,
        {
          ...pageLocationCountryData,
          country: item?.countryName,
          auto: false,
          mandatory: false,
          user_initiated: false,
          flow: "new",
        },
        ["clevertap"],
      );
    } else {
      setSelectedCountry(item);
      dispatch(setSelectedCountryFromList(item?.catelogID));
      if (item.catelogID !== COUNTRIES.INDIA) {
        setMandatoryError?.(false);
      }
      if (item.catelogID === COUNTRIES.SINGAPORE) {
        setShowDeliveryCityList(false);
      } else {
        dispatch(getInternationalCityList(item?.countryId));
      }
      setSearchValue("");
      setSuggestionSelected(false);
      trackEvent(
        cleverTapCommonEvents.locationCountrySelected,
        { ...pageLocationCountryData, country: item?.countryName, flow: "new" },
        ["clevertap"],
      );
    }
  };

  /**
   * To show the location lock Overlay for Mobile.
   */
  const onPincodeLocationClickHandler = () => {
    dispatch(setIsShowMobileLocationLockDrawerAction(false));
    dispatch(setIsShowMobileLocationLockAction(true));
  };
  /**
   *
   * @param {*} isFocused
   */
  const onFocusChangeHandler = (isFocused) => {
    if (isFocused) {
      setShowCountryList(false);
    }
  };

  useEffect(() => {
    // A location already captured by the caller wins over redux and the cookies. Capture
    // mode records a choice without committing it, so for the gift-finder gate those two
    // sources still hold the site's *current* location — reopening the step to edit it
    // would otherwise discard what the user picked and show the old one back.
    //
    // Gated on the captured *country*, not the pincode: confirming a country with no
    // delivery area is a valid answer there, and it is the case that needs this most —
    // without it, reopening a "USA, no city" answer reset the field to the site's catalog
    // and put the user back on India.
    if (locationCaptureMode && (capturedLocation?.geoId || capturedLocation?.pincode)) {
      const { pincode: capturedPincode, address: capturedAddress, geoId: capturedGeoId } = capturedLocation;
      const isCapturedDomestic = (capturedGeoId || catalogId) === COUNTRIES.INDIA;

      setSuggestionSelected(Boolean(capturedPincode));
      setSearchValue(
        capturedPincode
          ? isCapturedDomestic && capturedAddress
            ? `${capturedPincode}, ${capturedAddress}`
            : capturedAddress || String(capturedPincode)
          : "",
      );
      return;
    }

    // Domestic: prefer Redux (same source as PDP field), then cookies.
    if (catalogId === COUNTRIES.INDIA) {
      const domesticPincode = selectedDomesticLocation?.pincode || lastVisitedPincode;
      const domesticAddress = selectedDomesticLocation?.address || lastVisitedAddress;

      if (domesticPincode || domesticAddress) {
        setSuggestionSelected(true);
        // Last location picked from Google (area / locality): show the place only, without the pincode.
        const isGooglePlaceSelection = Boolean(CookieUtil.getCookie(LAST_VISITED_PLACE_TYPES));
        setSearchValue(
          domesticPincode && domesticAddress
            ? isGooglePlaceSelection
              ? domesticAddress
              : `${domesticPincode}, ${domesticAddress}`
            : domesticPincode || domesticAddress,
        );
        if (domesticAddress) {
          isLowAccuracyPlaceSelected().then(setIsLocationNudgeVisible);
        }
        return;
      }
    }

    // International: prefer Redux, then cookies.
    if (catalogId !== COUNTRIES.INDIA) {
      if (!isEmpty(selectedIntLocation?.location)) {
        setSuggestionSelected(true);
        setSearchValue(selectedIntLocation?.location);
        return;
      }

      if (lastVisitedIntLocation && lastVisitedIntPincode) {
        dispatch(setSelectedIntLocation({ pincode: lastVisitedIntPincode, location: lastVisitedIntLocation }));
        setSuggestionSelected(true);
        setSearchValue(`${lastVisitedIntLocation}`);
        return;
      }
    }

    setSearchValue(``);
    setSuggestionSelected(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    lastVisitedIntLocation,
    lastVisitedIntPincode,
    lastVisitedAddress,
    lastVisitedPincode,
    catalogId,
    selectedDomesticLocation?.pincode,
    selectedDomesticLocation?.address,
    selectedIntLocation?.location,
    selectedIntLocation?.pincode,
    capturedLocation?.pincode,
    capturedLocation?.address,
  ]);

  useEffect(() => {
    if (isLocationError || (!isMobile && pincodeError)) {
      const pdpValidationFields = isFromPDP
        ? {
            area: isInternational
              ? selectedIntLocation?.location
              : selectedDomesticLocation?.address || lastVisitedAddress,
            pincode: isInternational
              ? selectedIntLocation?.pincode
              : selectedDomesticLocation?.pincode || lastVisitedPincode,
            pid: product?.productId,
            sku_id: product?.skuCode,
          }
        : {};
      if (!isMobile && pincodeError) {
        const cleverTapData = {
          page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
          page_type: (typeof window !== "undefined" && window.fnpPageType) || "",
          validation_text: PLEASE_ENTER_DELIVERY_LOCATION,
          validation_for: validationFor.LOCATION_FIELD,
          ...pdpValidationFields,
          flow: "new",
        };

        trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
      } else {
        const cleverTapData = {
          page_name: pageName(isHome, isPLP, pathname, urlIdentifier),
          page_type: (typeof window !== "undefined" && window.fnpPageType) || "",
          validation_text: messageText,
          validation_for: validationFor.LOCATION_FIELD,
          ...pdpValidationFields,
          flow: "new",
        };

        trackEvent(cleverTapCommonEvents.validationViewed, cleverTapData, ["clevertap"]);
      }
    }
  }, [
    pincodeError,
    messageText,
    isMobile,
    isLocationError,
    isFromPDP,
    isInternational,
    selectedIntLocation,
    selectedDomesticLocation,
    product?.productId,
    product?.skuCode,
  ]);

  return (
    <>
      <div
        className={`${!isJoinedFieldLayout && !isInternationalMobilePDP ? "py-8" : ""} ${isMobile || isFromCheckout || !isJoinedFieldLayout ? "w-full" : "w-[90px]"} ${isInternationalCountry ? "hidden" : ""}`}
      >
        {!isInternationalCountry && (
          <CountryDropdown
            isShowCountryList={showCountryList}
            setShowCountryList={setShowCountryList}
            onCountrySelection={onCountrySelectionHandler}
            isFromPDP={isFromPDP || isInternationalMobilePDP}
            isSeparateFieldsDesign={isSeparateFieldsDesign}
            isError={!isMobile && (pincodeError || (isLocationError && isFromPDP))}
            classes="border-gray-300"
            isMobile={isMobile}
            isDisabled={isFromCheckout}
            isFromOutsideDrawer={isFromOutsideDrawer}
          />
        )}
      </div>

      {(!showCountryList || isFromPDP || isInternationalMobilePDP) && (
        <div
          className={`${isMobile || isFromCheckout || isInternationalCountry || !isJoinedFieldLayout ? "w-full" : "w-[calc(100%-90px)]"}  ${!isJoinedFieldLayout ? "pt-8" : ""}`}
          id="location-and-country-popup"
        >
          {isInternationalCountry || selectedCountry?.catelogID !== COUNTRIES.INDIA ? (
            <InternationalCitySuggestion
              isSuggestionSelected={suggestionSelected}
              setSuggestionSelected={setSuggestionSelected}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              isFromPDP={isFromPDP || isInternationalMobilePDP}
              isFromCheckout={isFromCheckout}
              onHandleContinueShopping={onHandleContinueShopping}
              showDeliveryCityList={showDeliveryCityList}
              isError={isLocationError || (!isMobile && pincodeError) || Boolean(serviceabilityErrorMessage)}
              messageText={
                serviceabilityErrorMessage || (!isMobile && pincodeError ? PLEASE_ENTER_DELIVERY_LOCATION : messageText)
              }
              messageClass={messageClass}
              isDisable={isDisable}
              onPincodeLocationClickHandler={onPincodeLocationClickHandler}
              urlIdentifier={urlIdentifier}
              cleverTapPDPCommonData={cleverTapPDPCommonData}
              isSeparateFieldsDesign={isSeparateFieldsDesign}
              isContinueButtonHidden={isContinueButtonHidden}
              onClearSavedAddressSelection={onClearSavedAddressSelection}
              selectedCountryId={selectedCountry?.countryId}
              serviceabilityErrorMessage={serviceabilityErrorMessage}
              isCountryChanged={isCountryChanged}
              selectedCountry={selectedCountry}
              isProductUnavailable={isProductUnavailable}
              onExploreAvailableProducts={onExploreAvailableProducts}
              onSuggestionsVisibilityChange={onSuggestionsVisibilityChange}
              isFromOutsideDrawer={isFromOutsideDrawer}
              isFromGiftFinderGate={isFromGiftFinderGate}
              locationCaptureMode={locationCaptureMode}
              onCaptureLocation={onCaptureLocation}
            />
          ) : (
            <PincodeLocationSuggestion
              isFromGiftFinderGate={isFromGiftFinderGate}
              locationCaptureMode={locationCaptureMode}
              onCaptureLocation={onCaptureLocation}
              selectedCountry={selectedCountry}
              isCountryChanged={isCountryChanged}
              isSuggestionSelected={suggestionSelected}
              setSuggestionSelected={setSuggestionSelected}
              searchValue={searchValue}
              setSearchValue={setSearchValue}
              onHandleContinueShopping={onHandleContinueShopping}
              isFromPDP={isFromPDP || isInternationalMobilePDP}
              isFromCheckout={isFromCheckout}
              isError={isLocationError || (!isMobile && pincodeError) || Boolean(serviceabilityErrorMessage)}
              messageText={
                serviceabilityErrorMessage || (!isMobile && pincodeError ? PLEASE_ENTER_DELIVERY_LOCATION : messageText)
              }
              messageClass={messageClass}
              isDisable={isDisable}
              onPincodeLocationClickHandler={onPincodeLocationClickHandler}
              urlIdentifier={urlIdentifier}
              cleverTapPDPCommonData={cleverTapPDPCommonData}
              setMandatoryError={setMandatoryError}
              isSeparateFieldsDesign={isSeparateFieldsDesign}
              isContinueButtonHidden={isContinueButtonHidden}
              onClearSavedAddressSelection={onClearSavedAddressSelection}
              {...{ isLocationNudgeVisible, setIsLocationNudgeVisible, serviceabilityErrorMessage }}
              isProductUnavailable={isProductUnavailable}
              onExploreAvailableProducts={onExploreAvailableProducts}
              onSuggestionsVisibilityChange={onSuggestionsVisibilityChange}
              onFocusChangeHandler={onFocusChangeHandler}
              isFromOutsideDrawer={isFromOutsideDrawer}
            />
          )}
        </div>
      )}
    </>
  );
}
LocationLock.propTypes = {
  onShowHideLocationLock: PropTypes.func,
  isFromPDP: PropTypes.bool,
  isFromCheckout: PropTypes.bool,
  onCountrySelection: PropTypes.func,
  fullProductDetails: PropTypes.object,
  isLocationError: PropTypes.bool,
  messageText: PropTypes.string,
  messageClass: PropTypes.string,
  isDisable: PropTypes.bool,
  setMandatoryError: PropTypes.func,
  isInternationalMobilePDP: PropTypes.bool,
  isSeparateFieldsDesign: PropTypes.bool,
  isContinueButtonHidden: PropTypes.bool,
  onClearSavedAddressSelection: PropTypes.func,
  isProductUnavailable: PropTypes.bool,
  onExploreAvailableProducts: PropTypes.func,
  onSuggestionsVisibilityChange: PropTypes.func,
  isFromOutsideDrawer: PropTypes.bool,
  /**
   * Presentation only: drops the country-change banner, the white confirm panel, its
   * border and BlankSpaceUI, and treats a location as optional. It does not change what
   * confirming does — see `locationCaptureMode` for that.
   */
  isFromGiftFinderGate: PropTypes.bool,
  /**
   * When supplied, confirming records the chosen location through `onCapture` instead of
   * committing it (no cookies, redux, serviceability or page refetch). The caller applies
   * it later. Absent for every ordinary caller, which keeps the commit path unchanged.
   */
  locationCaptureMode: PropTypes.shape({ onCapture: PropTypes.func.isRequired }),
  /**
   * A location already chosen through `locationCaptureMode` but not yet committed. Seeds
   * the field ahead of redux and the cookies, so reopening the step shows what was picked
   * rather than the site's current location.
   */
  capturedLocation: PropTypes.shape({
    pincode: PropTypes.string,
    address: PropTypes.string,
    /** Catalog of the captured country, which can differ from the page's own. */
    geoId: PropTypes.string,
  }),
};
